import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';

/**
 * SideMenu Component
 *
 * Mobile-only slide-in navigation menu that appears when the hamburger icon is clicked.
 * This component is controlled by jQuery in public/js/script.js which adds/removes
 * the 'side-menu-active' class to toggle visibility.
 *
 * The menu is hidden by default and slides in from the left on mobile devices.
 */
function SideMenu() {
  const { user, isAuthenticated, logout } = useAuth();
  const { changeTheme } = useTheme();

  // Initialize jQuery bindings after component mounts
  useEffect(() => {
    // Wait for jQuery to be available
    if (!window.$) {
      console.warn('[SideMenu] jQuery not loaded yet');
      return;
    }

    const $ = window.$;
    const sideMenuToggle = $("#sidemenu_toggle");
    const sideMenu = $(".side-menu");

    // Only bind if elements exist
    if (sideMenuToggle.length && sideMenu.length) {
      // Remove any existing handlers to prevent duplicates
      sideMenuToggle.off("click.sidemenu");
      $("#close_side_menu, #btn_sideNavClose").off("click.sidemenu");

      // Hamburger button click - Open menu
      sideMenuToggle.on("click.sidemenu", function (e) {
        e.preventDefault();
        $("body").addClass("overflow-hidden");
        sideMenu.addClass("side-menu-active");
        setTimeout(() => {
          $("#close_side_menu").fadeIn(300);
        }, 300);
      });

      // Close button and overlay click - Close menu
      $("#close_side_menu, #btn_sideNavClose").on("click.sidemenu", function (e) {
        e.preventDefault();
        $("body").removeClass("overflow-hidden");
        sideMenu.removeClass("side-menu-active");
        $("#close_side_menu").fadeOut(200);
        setTimeout(() => {
          $('.sideNavPages').removeClass('show');
          $('.fas').removeClass('rotate-180');
        }, 400);
      });

      // Close menu when clicking on actual nav links (not dropdown toggles)
      // Exclude elements with data-toggle="collapse" (dropdown toggles)
      $(".side-nav .nav-link").not('[data-toggle="collapse"]').on("click.sidemenu", function () {
        // Use a faster transition (200ms) for responsive but smooth close
        sideMenu.css('transition', 'transform 0.2s ease-out');

        $("body").removeClass("overflow-hidden");
        sideMenu.removeClass("side-menu-active");
        $("#close_side_menu").fadeOut(150); // Quick fade out

        // Reset to original transition after closing
        setTimeout(() => {
          sideMenu.css('transition', '');
        }, 250);
      });

      // ESC key to close menu
      $(document).off("keyup.sidemenu").on("keyup.sidemenu", (e) => {
        if (e.keyCode === 27 && sideMenu.hasClass("side-menu-active")) {
          $("body").removeClass("overflow-hidden");
          sideMenu.removeClass("side-menu-active");
          $("#close_side_menu").fadeOut(200);
          setTimeout(() => {
            $('.sideNavPages').removeClass('show');
            $('.fas').removeClass('rotate-180');
          }, 400);
        }
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('[SideMenu] jQuery bindings initialized');
      }
    }

    // Cleanup function to remove event handlers when component unmounts
    return () => {
      if (window.$) {
        $("#sidemenu_toggle").off("click.sidemenu");
        $("#close_side_menu, #btn_sideNavClose").off("click.sidemenu");
        $(".side-nav .nav-link").off("click.sidemenu");
        $(document).off("keyup.sidemenu");
      }
    };
  }, []); // Empty dependency array means this runs once after mount

  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
    // Close menu after logout
    if (window.$) {
      window.$('.side-menu').removeClass('side-menu-active');
      window.$('body').removeClass('overflow-hidden');
      window.$('#close_side_menu').fadeOut(200);
    }
  };

  const handleThemeChange = (newTheme) => {
    changeTheme(newTheme);
    // Keep menu open so user can see theme change immediately
  };

  return (
    <>
      {/* Side Menu Overlay - Dark background when menu is open */}
      <a
        href="/"
        onClick={(e) => e.preventDefault()}
        id="close_side_menu"
        style={{ display: 'none' }}
        aria-label="Close side menu"
      ></a>

      {/* Side Menu */}
      <div className="side-menu hidden">
        <div className="inner-wrapper">
          {/* Close button */}
          <span className="btn-close" id="btn_sideNavClose">
            <i></i>
            <i></i>
          </span>

          {/* Side Navigation */}
          <nav className="side-nav w-100">
            <ul className="navbar-nav">
              {/* Home */}
              <li className="nav-item">
                <Link className="nav-link" to="/">
                  HOME
                </Link>
              </li>

              {/* Collections */}
              <li className="nav-item">
                <Link className="nav-link" to="/product-listing">
                  COLLECTIONS
                </Link>
              </li>

              {/* Hall of Fame */}
              <li className="nav-item">
                <a
                  className="nav-link collapsePagesSideMenu"
                  data-toggle="collapse"
                  href="#sideNavPages1"
                  aria-expanded="false"
                  aria-controls="sideNavPages1"
                >
                  HALL OF FAME <i className="fas fa-chevron-down"></i>
                </a>
                <div id="sideNavPages1" className="collapse sideNavPages">
                  <ul className="navbar-nav mt-2">
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of Europe
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Made in England
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        The Big Cup
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of the World
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of Africa
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of the World (Women)
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Football */}
              <li className="nav-item">
                <a
                  className="nav-link collapsePagesSideMenu"
                  data-toggle="collapse"
                  href="#sideNavPages2"
                  aria-expanded="false"
                  aria-controls="sideNavPages2"
                >
                  FOOTBALL <i className="fas fa-chevron-down"></i>
                </a>
                <div id="sideNavPages2" className="collapse sideNavPages">
                  <ul className="navbar-nav mt-2">
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of Europe
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Made in England
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        The Big Cup
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Best of the World
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Finals
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Semi Finals
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Other Sports */}
              <li className="nav-item">
                <a
                  className="nav-link collapsePagesSideMenu"
                  data-toggle="collapse"
                  href="#sideNavPages3"
                  aria-expanded="false"
                  aria-controls="sideNavPages3"
                >
                  OTHER SPORTS <i className="fas fa-chevron-down"></i>
                </a>
                <div id="sideNavPages3" className="collapse sideNavPages">
                  <ul className="navbar-nav mt-2">
                    <li>
                      <Link className="nav-link" to="/product-listing">
                        Baseball
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/product-detail">
                        American Football
                      </Link>
                    </li>
                    <li>
                      <Link className="nav-link" to="/standalone">
                        Basketball
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Contact */}
              <li className="nav-item">
                <Link className="nav-link" to="/contact">
                  CONTACT
                </Link>
              </li>

              {/* FAQ */}
              <li className="nav-item">
                <Link className="nav-link" to="/faq">
                  FAQ
                </Link>
              </li>

              {/* Display Options */}
              <li className="nav-item">
                <a
                  className="nav-link collapsePagesSideMenu"
                  data-toggle="collapse"
                  href="#sideNavDisplayOptions"
                  aria-expanded="false"
                  aria-controls="sideNavDisplayOptions"
                >
                  DISPLAY OPTIONS <i className="fas fa-chevron-down"></i>
                </a>
                <div id="sideNavDisplayOptions" className="collapse sideNavPages">
                  <ul className="navbar-nav mt-2">
                    <li>
                      <button
                        className="nav-link"
                        onClick={() => handleThemeChange('light')}
                        style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
                      >
                        <i className="fas fa-sun" style={{ marginRight: '8px' }}></i>
                        Light Mode
                      </button>
                    </li>
                    <li>
                      <button
                        className="nav-link"
                        onClick={() => handleThemeChange('dark')}
                        style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
                      >
                        <i className="fas fa-moon" style={{ marginRight: '8px' }}></i>
                        Dark Mode
                      </button>
                    </li>
                    <li>
                      <button
                        className="nav-link"
                        onClick={() => handleThemeChange('yellow')}
                        style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
                      >
                        <i className="fas fa-palette" style={{ marginRight: '8px' }}></i>
                        Yellow Mode
                      </button>
                    </li>
                  </ul>
                </div>
              </li>

              {/* User Account Section */}
              <li className="nav-item" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                {isAuthenticated ? (
                  <>
                    <div style={{ padding: '0 15px', marginBottom: '10px' }}>
                      <strong>Hello, {user?.firstName || 'User'}</strong>
                    </div>
                    <Link className="nav-link" to="/orders">
                      My Orders
                    </Link>
                    <a className="nav-link" href="/" onClick={handleLogout}>
                      Logout
                    </a>
                  </>
                ) : (
                  <>
                    <Link className="nav-link" to="/register">
                      Register
                    </Link>
                    <Link className="nav-link" to="/login">
                      Login
                    </Link>
                  </>
                )}
              </li>
            </ul>
          </nav>

          {/* Footer info in side menu */}
          <div className="side-footer w-100" style={{ marginTop: 'auto', padding: '20px 15px', borderTop: '1px solid #eee' }}>
            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
              © {new Date().getFullYear()} Touchline Memories
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default SideMenu;
